import {
  assignNx5Device,
  assignNx5Version,
  createNx5Release,
  deleteNx5File,
  listNx5Directories,
  listNx5DirectoryFiles,
  listNx5Releases,
  listNx5Versions,
  uploadNx5Files,
} from '../adapters/Nx5Ota/Nx5OtaAdapter';

export const VERSION_PATTERN = /^[0-9]+\.[0-9]+\.[0-9]+(?:[.+-][0-9A-Za-z]+)*$/;

export class Nx5OtaApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = 'Nx5OtaApiError';
    this.status = status;
    this.data = data;
  }
}

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};

const readErrorMessage = (payload, fallback) =>
  payload?.data?.message ||
  payload?.message ||
  fallback;

export const getNx5OtaErrorMessage = (err, fallback = 'Request failed') => {
  if (err instanceof Nx5OtaApiError) return err.message;
  return readErrorMessage(err?.response?.data, err?.message || fallback);
};

const requestEnvelope = (request, fallback) =>
  new Promise((resolve, reject) => {
    request
      .then((res) => {
        const http = res?.status;
        if (http >= 200 && http < 300) {
          resolve(unwrap(res));
          return;
        }
        reject(new Nx5OtaApiError(readErrorMessage(res?.data, fallback), {
          status: http,
          data: unwrap(res),
        }));
      })
      .catch((err) => {
        if (err instanceof Nx5OtaApiError) {
          reject(err);
          return;
        }
        reject(new Nx5OtaApiError(getNx5OtaErrorMessage(err, fallback), {
          status: err?.response?.status,
          data: err?.response?.data,
        }));
      });
  });

export const fetchNx5Directories = (channel) =>
  requestEnvelope(listNx5Directories(channel), 'Could not load package directories');

export const fetchNx5Directory = (directory, channel) =>
  requestEnvelope(listNx5DirectoryFiles(directory, channel), 'Could not load directory files');

export const uploadNx5DirectoryFiles = (directory, entries, onUploadProgress, channel) => {
  const formData = new FormData();
  const relativePaths = entries.map((entry) => entry.relativePath);
  formData.append('relativePaths', JSON.stringify(relativePaths));
  entries.forEach((entry) => {
    const filename = entry.relativePath.split('/').pop() || entry.file.name;
    formData.append('files', entry.file, filename);
  });
  return requestEnvelope(
    uploadNx5Files(directory, formData, onUploadProgress, channel),
    'Could not upload files'
  );
};

export const removeNx5DirectoryFile = (id) =>
  requestEnvelope(deleteNx5File(id), 'Could not delete file');

export const fetchNx5Versions = () =>
  requestEnvelope(listNx5Versions(), 'Could not load versions');

export const fetchNx5Releases = (channel) =>
  requestEnvelope(listNx5Releases(channel), 'Could not load releases');

export const createNx5PackageRelease = (body) =>
  requestEnvelope(createNx5Release(body), 'Could not create release');

export const assignNx5Machine = (body) =>
  requestEnvelope(assignNx5Device(body), 'Could not assign device');

export const assignNx5DirectoryVersion = ({ directory, version, notes }) =>
  requestEnvelope(
    assignNx5Version({ directory, version, notes }),
    'Could not assign version'
  );

export const formatBytes = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) return '—';
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB'];
  let current = size / 1024;
  let unit = 0;
  while (current >= 1024 && unit < units.length - 1) {
    current /= 1024;
    unit += 1;
  }
  return `${current.toFixed(current >= 10 ? 0 : 1)} ${units[unit]}`;
};

export const relativePathForBrowserFile = (file) => {
  const fromFolder = file.webkitRelativePath;
  if (!fromFolder) return file.name;
  const parts = fromFolder.split('/').filter(Boolean);
  if (parts.length <= 1) return file.name;
  return parts.slice(1).join('/');
};
